<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    @change="editSingleSettingHandler"
    :is-form-fetched="isFormFetched"
    @save="saveCard"
  >
    <template #watermetersMigration.input>
      <base-button translation-path="button.runMigration" :loading="isMigrating" @click="runMigrationHandler" />
    </template>
    <template #waterMetersFeatureCard.input>
      <draggable-lists-manage-card
        left-title="admin.featureCardBilling"
        :layer-id="waterMetersLayerId"
        @update:hasAnyRequiredOnRightSide="hasAnyRequiredOnRightSide = $event"
        :has-edit-attributes="true"
      >
      </draggable-lists-manage-card>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DraggableListsManageCard from '@/components/DraggableListsManageCard';

export default {
  name: 'BillingsSettings',
  components: {
    AdminWrapper,
    DraggableListsManageCard,
  },
  data: () => ({
    isFormFetched: false,
    isMigrating: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.BILLINGS.verbose_name'),
    currentLayerSchema: get('layers/currentLayerSchema'),
    waterMetersDataSourceName: get('admin/modulesMapping@billing.watermeters_datasource_name'),
    waterMetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    isModuleEnabled: get('admin/settingsValues@billings_module_enabled'),
    isSmsModuleEnabled: get('admin/additionalModules@SMS.enabled'),
    isSmsSettingsEnabled: get('admin/settingsValues@sms_module_enabled'),
    moduleEnabler() {
      return {
        name: 'billings_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      const inputs = [
        {
          name: 'watermetersMigration',
          translationPath: 'admin.watermetersMigration',
          align: 'start',
          isCustom: true,
        },
        {
          name: 'billings_module_legalization_soon_threshold',
          translationPath: 'admin.billingsModuleLegalizationSoonThreshold',
          align: 'start',
          dataType: {
            name: 'integer',
          },
          subtitle: 'admin.billingsModuleLegalizationSoonThresholdSubtitle',
        },
      ];
      if (this.isSmsModuleEnabled && this.isSmsSettingsEnabled) {
        inputs.push({
          name: 'billing_sms_sending_enabled',
          translationPath: 'admin.smsDispatch',
          dataType: {
            name: 'switch',
          },
          subtitle: 'admin.smsDispatchSubtitle',
        });
      }
      inputs.push({
        name: 'waterMetersFeatureCard',
        translationPath: 'admin.featureCardBilling',
        isCustom: true,
        subtitle: this.$i18n.t('admin.setFeatureCard', { type: this.$i18n.t('admin.billingType') }),
        align: 'start',
      });
      return inputs;
    },
  },
  methods: {
    editLayer: call('layers/editLayer'),
    editSingleSetting: call('admin/editSingleSetting'),
    getDataSourceForm: call('layers/getDataSourceForm'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    runMigration: call('billings/migrateBillingsData'),
    checkMigrationStatus: call('celeryTasks/getCeleryTaskStatus'),
    editSingleSettingHandler(value, name) {
      if (name === 'billings_module_legalization_soon_threshold' || name === 'billing_sms_sending_enabled') {
        this.editSingleSetting({
          key: name,
          body: {
            value,
          },
        });
      } else {
        return;
      }
    },
    async init() {
      await this.getModulesMapping();
      await Promise.all([
        this.getDataSourceForm(this.waterMetersDataSourceName),
        this.getFeaturesLayer(this.waterMetersLayerId),
        this.getLayerMetadata({ dataSource: this.waterMetersDataSourceName }),
      ]);
      this.isFormFetched = true;
    },
    async saveCard() {
      await this.editLayer({
        layer_type: 'features_layers',
        layer_id: this.waterMetersLayerId,
        body: {
          form_schema: {
            elements: this.currentLayerSchema,
          },
        },
      });
    },
    async runMigrationHandler() {
      try {
        this.isMigrating = true;
        const taskId = (await this.runMigration({ params: { background: true } })).data.data.tid;
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.migratingWatermeters'),
        });
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            try {
              const status = await this.checkMigrationStatus(taskId);
              if (['REVOKED', 'FAILURE'].includes(status)) {
                this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
                this.$store.set('snackbar/PUSH_ERROR!', {
                  message: this.$i18n.t('snackbar.migratingWatermetersError'),
                });
                this.isMigrating = false;
                throw Error(`Celery task status checker: ${status}`);
              }
              return status === 'SUCCESS';
            } catch (error) {
              console.log('error', error);
            }
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
              message: this.$i18n.t('snackbar.migrationWatermetersCompleted'),
            });
            this.isMigrating = false;
          },
          interval: 5000,
        });
      } catch (error) {
        console.log('error', error);
        this.isMigrating = false;
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
